import { type Dispatch, type SetStateAction } from 'react'
import type { RevisionDiff } from '~/api/types'
import { Card } from '~/components/ui/card'
import { cn } from '~/lib/utils'
import { TAB_ITEMS, type TabKey } from './constants'
import { toggle } from './helpers'
import { AddedList } from './AddedList'
import { ModifiedList } from './ModifiedList'
import { RemovedList } from './RemovedList'

// 항목별 diff 표시·선택. 요약 스탯·반영 안내·탭과 수정/추가/삭제 리스트를 렌더하고
// 선택 토글을 상위 선택 상태에 반영한다(반영/호출은 패널·훅이 담당).
export function DiffViewer({
    result,
    summary,
    noChanges,
    tab,
    onTab,
    selModified,
    setSelModified,
    selAdded,
    setSelAdded,
    selRemoved,
    setSelRemoved,
}: {
    result: RevisionDiff
    summary: RevisionDiff['summary']
    noChanges: boolean
    tab: TabKey
    onTab: (tab: TabKey) => void
    selModified: Set<string>
    setSelModified: Dispatch<SetStateAction<Set<string>>>
    selAdded: Set<number>
    setSelAdded: Dispatch<SetStateAction<Set<number>>>
    selRemoved: Set<string>
    setSelRemoved: Dispatch<SetStateAction<Set<string>>>
}) {
    return (
        <>
            {/* 요약 스탯카드 */}
            <div className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
                <SummaryStat
                    label='추가'
                    value={summary.added}
                    tone='success'
                />
                <SummaryStat
                    label='수정'
                    value={summary.modified}
                    tone='warning'
                />
                <SummaryStat
                    label='삭제'
                    value={summary.removed}
                    tone='danger'
                />
                <SummaryStat
                    label='동일'
                    value={summary.unchanged}
                    tone='gray'
                />
            </div>

            {noChanges ? (
                <Card className='bg-muted/40 p-6 text-center'>
                    <p className='text-foreground text-sm font-bold'>
                        변경분이 없습니다 (현행과 동일)
                    </p>
                    <p className='text-muted-foreground mt-1 text-xs'>
                        업로드한 개정본은 현재 카탈로그와 차이가 없어요.
                    </p>
                </Card>
            ) : (
                <>
                    {/* 반영 안내 */}
                    <div className='bg-info-subtle border-info/30 rounded-[var(--radius)] border p-3'>
                        <p className='text-info text-sm'>
                            반영할 <strong>추가·수정</strong> 항목을 선택한 뒤
                            하단 <strong>선택 반영</strong>을 누르세요. 삭제
                            항목은 기존 평가가 참조할 수 있어 자동 반영하지
                            않아요.
                        </p>
                    </div>

                    {/* 탭 */}
                    <div
                        role='tablist'
                        className='border-border flex gap-1 border-b'>
                        {TAB_ITEMS.map((t) => {
                            const count =
                                t.key === 'modified'
                                    ? summary.modified
                                    : t.key === 'added'
                                      ? summary.added
                                      : summary.removed
                            const active = tab === t.key
                            return (
                                <button
                                    key={t.key}
                                    type='button'
                                    role='tab'
                                    aria-selected={active}
                                    onClick={() => onTab(t.key)}
                                    className={cn(
                                        '-mb-px border-b-2 px-4 py-2 text-sm font-bold transition-colors',
                                        active
                                            ? 'border-primary text-primary'
                                            : 'text-muted-foreground hover:text-foreground border-transparent'
                                    )}>
                                    {t.label}{' '}
                                    <span className='text-xs'>{count}</span>
                                </button>
                            )
                        })}
                    </div>

                    {tab === 'modified' ? (
                        <ModifiedList
                            items={result.modified}
                            selected={selModified}
                            onToggle={(id) =>
                                setSelModified((prev) => toggle(prev, id))
                            }
                            onToggleAll={(checked) =>
                                setSelModified(
                                    checked
                                        ? new Set(
                                              result.modified.map((m) => m.id)
                                          )
                                        : new Set()
                                )
                            }
                        />
                    ) : null}
                    {tab === 'added' ? (
                        <AddedList
                            items={result.added}
                            selected={selAdded}
                            onToggle={(i) =>
                                setSelAdded((prev) => toggle(prev, i))
                            }
                            onToggleAll={(checked) =>
                                setSelAdded(
                                    checked
                                        ? new Set(result.added.map((_, i) => i))
                                        : new Set()
                                )
                            }
                        />
                    ) : null}
                    {tab === 'removed' ? (
                        <RemovedList
                            items={result.removed}
                            selected={selRemoved}
                            onToggle={(id) =>
                                setSelRemoved((prev) => toggle(prev, id))
                            }
                        />
                    ) : null}
                </>
            )}
        </>
    )
}

// ── 요약 ─────────────────────────────────────────────────────────────────────

const SUMMARY_TONE: Record<
    'success' | 'warning' | 'danger' | 'gray',
    { accent: string; value: string }
> = {
    success: { accent: 'border-t-success', value: 'text-success' },
    warning: { accent: 'border-t-warning', value: 'text-warning' },
    danger: { accent: 'border-t-destructive', value: 'text-destructive' },
    gray: { accent: 'border-t-transparent', value: 'text-muted-foreground' },
}

function SummaryStat({
    label,
    value,
    tone,
}: {
    label: string
    value: number
    tone: keyof typeof SUMMARY_TONE
}) {
    const t = SUMMARY_TONE[tone]
    return (
        <Card className={cn('border-t-2 p-4', t.accent)}>
            <div className='krds-label'>{label}</div>
            <div className={cn('krds-heading-m mt-1', t.value)}>{value}</div>
        </Card>
    )
}
